import { useContext, useMemo } from 'react';

import { Context as RolesContext } from '../api/roles/context';
import { Context as AuthContext } from '../api/auth/context';
import { checkGrant } from './PermissionsContext';
import { PermissionKey } from './permissions.config';
import { resolveEffectivePermissions } from './effectivePermissions';

/**
 * Build the current user's grant set from RolesContext + AuthContext.
 *
 * The role's permissions array stores STRING KEYS (possibly with wildcards)
 * — no more legacy numeric IDs. Wildcards are expanded into concrete keys
 * so checks become O(1) Set lookups.
 */
function useUserGrants(): { grants: Set<string>; isSuperUser: boolean } {
    const rolesContext = useContext(RolesContext);
    const authContext = useContext(AuthContext);

    return useMemo(() => {
        const user = authContext?.currUser;
        if (!user) return { grants: new Set<string>(), isSuperUser: false };
        if (user.isSuperUser) return { grants: new Set<string>(), isSuperUser: true };

        const userRoleId = user.role;
        const rolesList = rolesContext?.data || [];
        if (!userRoleId || !rolesList.length) return { grants: new Set<string>(), isSuperUser: false };

        const role = rolesList.find((cell: any) => cell._id == userRoleId);

        // The role is the baseline; this user's own additions and revocations
        // adjust it. Same helper the authorize middleware uses, so what the UI
        // offers and what the server allows cannot drift apart.
        const grants = resolveEffectivePermissions(role?.permissions, {
            permissionsAdd: (user as any)?.permissionsAdd,
            permissionsRevoke: (user as any)?.permissionsRevoke
        });
        return { grants, isSuperUser: false };
    }, [authContext?.currUser, rolesContext?.data]);
}

/**
 * Single-key permission check.
 *
 * @example
 *   const canEdit = usePermission('service.flight:edit');
 *   if (canEdit) showEditButton();
 */
export function usePermission(key: PermissionKey): boolean {
    const { grants, isSuperUser } = useUserGrants();
    if (isSuperUser) return true;
    return checkGrant(grants, key);
}

/**
 * Composite permission helper. Returns an object with `.any()` and `.all()`
 * for multi-key checks, and is callable as a function for single-key checks
 * with optional wildcard support.
 *
 * @example
 *   const can = useCan();
 *   if (can('service.flight:edit')) { ... }
 *   if (can.any(['voucher:cancel', 'voucher:issue'])) { ... }
 *   if (can.all(['travel_file:edit', 'service.flight:edit'])) { ... }
 *   if (can('service.*:view')) { ... }   // wildcard at check time
 */
type CanFn = ((key: string) => boolean) & {
    any: (keys: string[]) => boolean;
    all: (keys: string[]) => boolean;
};

export function useCan(): CanFn {
    const { grants, isSuperUser } = useUserGrants();

    const can = ((key: string) => {
        if (isSuperUser) return true;
        return checkGrant(grants, key);
    }) as CanFn;

    can.any = (keys: string[]) => {
        if (isSuperUser) return true;
        return keys.some((k) => checkGrant(grants, k));
    };

    can.all = (keys: string[]) => {
        if (isSuperUser) return true;
        return keys.every((k) => checkGrant(grants, k));
    };

    return can;
}

/**
 * True only for the internal super-user.
 *
 * Distinct from `usePermission`/`useCan`, which return true for a super-user *and* for anyone whose
 * role carries the key. Use this for screens that are internal-only regardless of role — the ticket
 * board, which reads across every tenant, is the case it exists for. Mirrors the backend's
 * server-side `ticketScope()` check, which pins non-super-users to their own tenant.
 */
export function useIsSuperUser(): boolean {
    const { isSuperUser } = useUserGrants();
    return isSuperUser;
}

/**
 * Debug helper — returns the full grant list for the current user.
 * Useful in dev tools to inspect what the user can actually do.
 */
export function usePermissionDebug(): { grants: string[]; isSuperUser: boolean } {
    const { grants, isSuperUser } = useUserGrants();
    return { grants: [...grants].sort(), isSuperUser };
}
